import { motion } from "framer-motion";
import { calculateMonthsBetween, hasContent } from "@/lib/utils";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { ExternalLink, Github } from "lucide-react";
import SkillCategoriesDisplay from "./skills/components/SkillCategoriesDisplay";
import GitHubStatsDisplay from "./GitHubStatsDisplay";
import { Link } from "wouter";
import { companies } from '../../../db';
import { createCompanySlug, formatDateRange } from "@/lib/companyUtils";
import { useActivitySpecificProjectSkills } from "@/hooks/useActivitySpecificProjectSkills";
import ErrorBoundary from "./ErrorBoundary";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useState, useEffect } from "react";

interface ProjectDetailsProps {
  project: {
    title: string;
    company: string;
    startDate: string;
    endDate: string;
    description: string;
    // Legacy format
    tech?: { name: string; weight: number }[];
    skills?: { name: string; weight: number }[];
    // New categorized format
    categorizedSkills?: {
      profession: { name: string; weight: number }[];
      language: { name: string; weight: number }[];
      tools: { name: string; weight: number }[];
      lead: { name: string; weight: number }[];
    };
    goal: string;
    details?: string; // Legacy format
    detailsFile?: string; // New format - path to markdown file
    metrics?: string[];
    role: string;
    teamSize?: string;
    isProfessional?: boolean;
    git?: string;
    link?: string;
    status?: string;
    activityId?: number | number[] | null; // Project's associated activities for skill calculations
  };
  activityId?: number; // Optional activity context for time-bounded skill calculations
}

export default function ProjectDetails({ project, activityId }: ProjectDetailsProps) {
  const [markdownContent, setMarkdownContent] = useState<string>('');
  const [isLoadingMarkdown, setIsLoadingMarkdown] = useState<boolean>(false);
  const [markdownError, setMarkdownError] = useState<string | null>(null);

  // Secure function to validate detailsFile parameter
  const validateDetailsFile = (fileName: string): boolean => {
    // Only allow alphanumeric characters, hyphens, underscores, and dots
    // Prevent directory traversal attacks (../, ..\, etc.)
    const safeFileNamePattern = /^[a-zA-Z0-9._-]+\.md$/;
    return safeFileNamePattern.test(fileName) && 
           !fileName.includes('..') && 
           !fileName.includes('/') && 
           !fileName.includes('\\');
  };

  // Load markdown content if detailsFile is provided
  useEffect(() => {
    if (project.detailsFile) {
      // Reset previous state
      setMarkdownError(null);
      setMarkdownContent('');

      // Validate the file name for security
      if (!validateDetailsFile(project.detailsFile)) {
        console.error('Invalid detailsFile parameter:', project.detailsFile);
        setMarkdownError('Invalid file reference');
        return;
      }

      setIsLoadingMarkdown(true);
      // Load markdown file from project-details directory
      fetch(`/db/project-details/${project.detailsFile}`)
        .then(response => {
          if (!response.ok) {
            if (response.status === 404) {
              throw new Error('Details file not found');
            } else if (response.status >= 500) {
              throw new Error('Server error loading details');
            } else {
              throw new Error(`Failed to load details (${response.status})`);
            }
          }
          return response.text();
        })
        .then(content => {
          if (!content.trim()) {
            setMarkdownError('Details file is empty');
            return;
          }
          setMarkdownContent(content);
        })
        .catch(error => {
          console.error('Error loading markdown file:', error);
          setMarkdownError(error.message || 'Error loading project details');
        })
        .finally(() => {
          setIsLoadingMarkdown(false);
        });
    }
  }, [project.detailsFile]);

  // Function to get company data by name
  const getCompanyByName = (companyName: string) => {
    return companies.find(company => company.name === companyName);
  };

  // Use the activity-specific skills hook to get GitHub-enhanced language data
  // and optionally time-bounded skills for the specific activity context
  const { skillsByCategory, isLoading: skillsLoading, languagesFromGitHub, effectivePeriod } = useActivitySpecificProjectSkills({ 
    project: {
      git: project.git,
      startDate: project.startDate,
      endDate: project.endDate === 'Present' ? null : project.endDate,
      activityId: project.activityId || null,
      categorizedSkills: project.categorizedSkills,
      skills: project.skills
    }, 
    activityId 
  });

  const company = getCompanyByName(project.company);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="space-y-6"
    >
      <div className="relative pl-4">
        <div className="inline-block px-4 py-2 mb-4 relative">
          <div className="absolute inset-0 bg-gradient-to-r from-primary/50 via-primary/20 to-transparent rounded-lg blur-lg"></div>
          <h2 className="text-2xl font-bold relative z-10">{project.title}</h2>
        </div>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Project Overview</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Only show company information for professional projects */}
          {project.isProfessional !== false && (
            <div>
              <h3 className="font-semibold mb-2">Company</h3>
              <div className="flex items-center gap-2">
                {company && (
                  <Link href={`/professional/${createCompanySlug(company.name)}`}>
                    <Avatar className="h-6 w-6 bg-transparent cursor-pointer hover:scale-110 transition-transform">
                      <AvatarImage
                        src={company.logoPath}
                        alt={`${company.name} logo`}
                        className="object-cover"
                      />
                      <AvatarFallback className="bg-primary/10 text-xs">{company.logoText}</AvatarFallback>
                    </Avatar>
                  </Link>
                )}
                <p className="text-muted-foreground">{project.company}</p>
              </div>
            </div>
          )}
          <div>
            <h3 className="font-semibold mb-2">Duration</h3>
            {activityId && effectivePeriod ? (
              <div>
                <p className="text-muted-foreground">
                  {calculateMonthsBetween(effectivePeriod.startDate, effectivePeriod.endDate || 'Present')} months
                  {(!effectivePeriod.endDate) && (
                    <span className="ml-2 text-primary">(ongoing)</span>
                  )}
                  <span className="ml-2 text-xs text-muted-foreground/70">
                    (activity period)
                  </span>
                </p>
                <p className="text-sm text-muted-foreground/80 mt-1">
                  {formatDateRange(effectivePeriod.startDate, effectivePeriod.endDate)}
                </p>
                <p className="text-xs text-muted-foreground/70 mt-1">
                  Full project: {calculateMonthsBetween(project.startDate, project.endDate)} months
                </p>
                <p className="text-xs text-muted-foreground/60">
                  {formatDateRange(project.startDate, project.endDate === 'Present' ? null : project.endDate)}
                </p>
              </div>
            ) : (
              <div>
                <p className="text-muted-foreground">
                  {calculateMonthsBetween(project.startDate, project.endDate)} months
                  {(!project.endDate || project.endDate === "Present") && (
                    <span className="ml-2 text-primary">(ongoing)</span>
                  )}
                </p>
                <p className="text-sm text-muted-foreground/80 mt-1">
                  {formatDateRange(project.startDate, project.endDate === 'Present' ? null : project.endDate)}
                </p>
              </div>
            )}
          </div>
          <div>
            <h3 className="font-semibold mb-2">Role</h3>
            <p className="text-muted-foreground">{project.role}</p>
          </div>
          {project.teamSize && (
            <div>
              <h3 className="font-semibold mb-2">Team Size</h3>
              <p className="text-muted-foreground">{project.teamSize}</p>
            </div>
          )}
          {project.status && (
            <div>
              <h3 className="font-semibold mb-2">Status</h3>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-muted-foreground capitalize">{project.status}</span>
              </div>
            </div>
          )}
          {(project.git || project.link) && (
            <div>
              <h3 className="font-semibold mb-2">Source Code</h3>
              <div className="space-y-3">
                <div className="flex gap-2">
                  {project.git && !project.git.includes("not") && !project.git.includes("inner source") && (
                    <Button variant="outline" size="sm" asChild>
                      <a href={project.git} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2">
                        <Github className="h-4 w-4" />
                        Open Source
                      </a>
                    </Button>
                  )}
                  {project.link && !project.link.includes("not") && !project.link.includes("inner source") && project.link !== project.git && (
                    <Button variant="outline" size="sm" asChild>
                      <a href={project.link} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2">
                        <ExternalLink className="h-4 w-4" />
                        View Project
                      </a>
                    </Button>
                  )}
                  {(project.git?.includes("inner source") || project.link?.includes("inner source")) && (
                    <span className="text-muted-foreground text-sm italic">Code not available - inner source</span>
                  )}
                  {(project.git?.includes("not") || project.link?.includes("not")) && (
                    <span className="text-muted-foreground text-sm italic">Code not available</span>
                  )}
                </div>
                
                {/* GitHub Stats */}
                {project.git && project.git.includes("github.com") && !project.git.includes("not") && !project.git.includes("inner source") && (
                  <ErrorBoundary fallback={({ error, resetError }) => (
                    <div className="p-4 border border-destructive/20 rounded-lg bg-destructive/5">
                      <p className="text-sm text-muted-foreground">GitHub stats unavailable</p>
                    </div>
                  )}>
                    <GitHubStatsDisplay repoUrl={project.git} />
                  </ErrorBoundary>
                )}
              </div>
            </div>
          )}
          <div>
            <h3 className="font-semibold mb-4">Skills & Technologies</h3>
            {skillsLoading ? (
              <div className="flex items-center justify-center p-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                <span className="ml-2 text-muted-foreground">Enhancing skills with GitHub data...</span>
              </div>
            ) : skillsByCategory ? (
              <SkillCategoriesDisplay 
                skillsByCategory={skillsByCategory}
                displayMode="weight"
                compactMode={false}
                animated={true}
                languagesFromGitHub={languagesFromGitHub}
                calculationType="project"
                showHeader={false}
              />
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                No skills data available
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {hasContent(project.goal) && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Goal</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">{project.goal}</p>
          </CardContent>
        </Card>
      )}

      {(hasContent(project.details) || project.detailsFile) && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Details</CardTitle>
          </CardHeader>
          <CardContent>
            {project.detailsFile ? (
              isLoadingMarkdown ? (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                  <span>Loading details...</span>
                </div>
              ) : markdownError ? (
                <div className="p-4 border border-destructive/20 rounded-lg bg-destructive/5">
                  <p className="text-sm text-destructive font-medium">Unable to load details</p>
                  <p className="text-xs text-muted-foreground mt-1">{markdownError}</p>
                </div>
              ) : markdownContent ? (
                <ErrorBoundary fallback={({ error, resetError }) => (
                  <div className="p-4 border border-destructive/20 rounded-lg bg-destructive/5">
                    <p className="text-sm text-destructive font-medium">Error rendering details</p>
                    <p className="text-xs text-muted-foreground mt-1">Failed to display markdown content</p>
                    <button 
                      onClick={resetError}
                      className="mt-2 text-xs text-primary hover:underline"
                    >
                      Try again
                    </button>
                  </div>
                )}>
                  <div className="prose prose-sm max-w-none dark:prose-invert text-muted-foreground">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        p: ({ children }) => <p className="mb-4 last:mb-0">{children}</p>,
                        strong: ({ children }) => <strong className="font-semibold text-foreground">{children}</strong>,
                      }}
                    >
                      {markdownContent}
                    </ReactMarkdown>
                  </div>
                </ErrorBoundary>
              ) : (
                <p className="text-muted-foreground italic">No details available</p>
              )
            ) : (
              <p className="text-muted-foreground whitespace-pre-line">
                {project.details}
              </p>
            )}
          </CardContent>
        </Card>
      )}
      
      {hasContent(project.metrics) && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Metrics</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="list-disc list-inside space-y-2 text-muted-foreground">
              {project.metrics?.map((metric, index) => (
                <li key={index}>{metric}</li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </motion.div>
  );
}